import type { Product, ReceiptItem } from "../../types/receipt";
import { normalizedMeasure, units } from "../../types/receipt";
import { money } from "../../lib/finance";

// The editable fields for one line of a receipt: what it was, how much, and the pack size that
// makes two prices comparable.

export function comparisonLabel(item: ReceiptItem, currency: string) {
  const normalized = normalizedMeasure(item.measure, item.unit);
  const total = Number(item.total);
  if (!normalized || !item.total.trim() || !Number.isFinite(total) || total < 0)
    return "Set comparison quantity";
  return `${money(total / normalized.measure, currency)} / ${normalized.unit}`;
}

export default function ItemFields({
  item,
  change,
  tracking = false,
  products = [],
}: {
  item: ReceiptItem;
  change: (patch: Partial<ReceiptItem>) => void;
  tracking?: boolean;
  products?: Product[];
}) {
  const normalized = normalizedMeasure(item.measure, item.unit);
  return (
    <div className="receipt-item-fields">
      <label>
        Receipt wording
        <input
          value={item.original}
          maxLength={160}
          onChange={(e) => change({ original: e.target.value })}
        />
      </label>
      {tracking && (
        <>
          <label>
            Match an existing product
            <select
              aria-label="Match existing product"
              value={
                products.find(
                  (p) =>
                    p.name === item.name &&
                    p.brand === item.brand &&
                    p.group === item.group,
                )?.id || ""
              }
              onChange={(e) => {
                const p = products.find((p) => p.id === e.target.value);
                if (p) change({ name: p.name, brand: p.brand, group: p.group });
              }}
            >
              <option value="">New / custom product</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.brand || p.group}
                </option>
              ))}
            </select>
          </label>
          <label>
            Product group
            <input
              value={item.group}
              maxLength={80}
              list="receipt-groups"
              placeholder="e.g. Paper towels"
              onChange={(e) => change({ group: e.target.value })}
            />
          </label>
          <label>
            Product name
            <input
              value={item.name}
              maxLength={160}
              onChange={(e) => change({ name: e.target.value })}
            />
          </label>
          <label>
            Brand / variant
            <input
              value={item.brand}
              maxLength={160}
              placeholder="e.g. Brand A · 2-ply"
              onChange={(e) => change({ brand: e.target.value })}
            />
          </label>
        </>
      )}
      <div className="expense-fields">
        <label>
          Quantity bought
          <input
            inputMode="decimal"
            value={item.quantity}
            onChange={(e) => change({ quantity: e.target.value })}
          />
        </label>
        <label>
          Line total
          <input
            inputMode="decimal"
            value={item.total}
            onChange={(e) => change({ total: e.target.value })}
          />
        </label>
      </div>
      {tracking && (
        <>
          <label>
            Pack description
            <input
              value={item.pack}
              maxLength={160}
              placeholder="e.g. 4 rolls × 100 sheets"
              onChange={(e) => change({ pack: e.target.value })}
            />
          </label>
          <div className="expense-fields">
            <label>
              Total comparison quantity
              <input
                inputMode="decimal"
                value={item.measure}
                placeholder="e.g. 400"
                onChange={(e) => change({ measure: e.target.value })}
              />
            </label>
            <label>
              Unit
              <select
                value={item.unit}
                onChange={(e) =>
                  change({
                    unit: e.target.value,
                    ...(!e.target.value ? { measure: "" } : {}),
                  })
                }
              >
                {units.map((u) => (
                  <option key={u} value={u}>
                    {u || "Unknown"}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <small className="muted">
            {normalized && item.total
              ? `${money(Number(item.total) / normalized.measure)} per ${normalized.unit} (in receipt currency)`
              : "For countable items, use quantity as the comparison quantity and each as the unit."}
          </small>
        </>
      )}
    </div>
  );
}
